import React, { useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Modal } from '@mui/material';
import { FilterPanel, FilterPanelControls, useIsMobileWidth } from '@/components/common/filter';
import { FORM_COLOR } from '@/pages/Seminar/components/form/formStyles';
import { designSystem } from '@/styles/theme';

/**
 * 공용 확인·입력 창 — 폰(≤768px)은 아래에서 올라오는 시트, PC 는 가운데 작은 창(UI 가이드 5장).
 *
 * - 폰 시트는 공용 `FilterPanel` 시트를 그대로 쓴다: 위쪽 반경 20·잡는 막대·제목 줄, 하단 버튼은 시트 안에 붙어 있고(sticky 와 같은 자리),
 *   `document.body` 포털·몸통 스크롤 잠금(`body[data-scroll-lock]`)·바깥 누르기·아래로 끌기·뒤로가기·Esc 로 닫힌다.
 *   뒤로가기로 닫히게 하려고 열 때 방문 기록을 한 칸 쌓는다 — 그래서 창을 닫고 이동하는 동작(로그아웃·삭제 뒤 이동)은
 *   반드시 `controls.done(after)` 로 부른다. 쌓은 칸을 먼저 되돌린 뒤 after 를 부르므로 이동이 되돌려지지 않는다.
 * - PC 창은 MUI Modal(modal 층, Esc·바깥 누르기 닫힘, 포커스 가두기).
 * - `disableScrollLock`: 몸통을 잠그지 않는다(PC·폰 둘 다). 대화방처럼 위 바·입력줄이 sticky 인 화면은 잠금이 걸리면
 *   창이 떠 있는 동안 그 줄들이 화면 밖으로 밀려난다(html overflow:clip). 폰 시트는 `body[data-scroll-lock]` 표시만 먼저 달아
 *   공용 시트가 "이미 잠김"으로 보고 몸통 고정을 건너뛰게 한다 — 당겨서 새로고침·스와이프 뒤로가기는 그 표시를 보고 그대로 쉰다.
 * - 열려 있을 때만 그린다(둘 다 닫히면 DOM 이 없다 — 상시 role=dialog 는 스와이프 뒤로가기를 막는다).
 */

export interface SheetDialogControls {
    /** 창만 닫는다(onClose) */
    close: () => void;
    /** 창을 닫고(폰은 쌓은 방문 기록 칸까지 되돌린 뒤) after 를 부른다 — 확인·이동처럼 닫은 다음 할 일이 있을 때 */
    done: (after: () => void) => void;
}

export interface SheetDialogProps {
    open: boolean;
    title: React.ReactNode;
    /** 바깥 누르기·끌기·뒤로가기·Esc·[닫기] — 창만 닫힘 */
    onClose: () => void;
    /** 처리 중 — 바깥 누르기·끌기·뒤로가기·Esc 로 닫히지 않게 */
    busy?: boolean;
    /** 하단 버튼 줄(폰 시트 안 고정 / PC 창 아래). `SheetDialogActions` + `SheetDialogButton` 으로 그린다 */
    footer?: (controls: SheetDialogControls) => React.ReactNode;
    /** PC 창 폭(기본 400) */
    pcWidth?: number;
    /** 몸통 스크롤 잠금을 끈다(위 설명·ConfirmModal 설명 참고) */
    disableScrollLock?: boolean;
    /** 폰 시트 층(기본 popover — 다른 시트·창 위에도 뜬다) */
    sheetZIndex?: number;
    children?: React.ReactNode | ((controls: SheetDialogControls) => React.ReactNode);
}

// ───────── 모양(폰·PC 공통) ─────────

/** 창 본문 설명 — 본문 15px 보조색 */
export const SheetDialogText = styled.p`
    margin: 0;
    font-size: 15px;
    line-height: 1.6;
    color: ${FORM_COLOR.sub};
    white-space: pre-line;
    word-break: keep-all;
    overflow-wrap: anywhere;
`;

/** 하단 버튼 줄 — 버튼은 폭을 똑같이 나눈다(테두리 있는 버튼도 같은 폭이 되게 flex 대신 같은 칸 grid) */
export const SheetDialogActions = styled.div`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    gap: 8px;
    width: 100%;
`;

/** 하단 버튼 — 높이 52·반경 14·글자 16px 800(가이드 4장). tone: primary 청록(accent 로 바꿀 수 있음) / danger 빨강(#E11D48 하나) / 없으면 흰 바탕 테두리 */
export const SheetDialogButton = styled('button', { shouldForwardProp: (prop: string) => prop !== 'tone' && prop !== 'accent' })<{ tone?: 'primary' | 'danger'; accent?: string }>`
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    height: 52px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 16px;
    border-radius: 14px;
    border: ${({ tone }) => (tone ? 'none' : `1px solid ${FORM_COLOR.border}`)};
    background: ${({ tone, accent }) => (tone === 'danger' ? FORM_COLOR.danger : tone === 'primary' ? (accent ?? FORM_COLOR.primary) : '#fff')};
    color: ${({ tone }) => (tone ? '#fff' : FORM_COLOR.sub)};
    font-family: inherit;
    font-size: 16px;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:disabled {
        cursor: default;
        ${({ tone }) => (tone ? `background: ${FORM_COLOR.primaryDisabled};` : 'opacity: 0.5;')}
    }
    &:focus-visible {
        outline: 2px solid ${FORM_COLOR.primary};
        outline-offset: 2px;
    }
`;

// ───────── PC: 가운데 작은 창 ─────────

const PcCard = styled.div`
    display: flex;
    flex-direction: column;
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 64px);
    box-sizing: border-box;
    padding: 24px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 14px 36px rgba(20, 30, 28, 0.18);
    outline: none;
`;

const PcTitle = styled.h2`
    flex-shrink: 0;
    margin: 0 0 12px;
    font-size: 19px;
    font-weight: 800;
    line-height: 1.4;
    color: ${FORM_COLOR.text};
    white-space: pre-line;
    word-break: keep-all;
    overflow-wrap: anywhere;
`;

/** 본문 — 길면 안에서 스크롤. 칸 테두리·포커스 선이 스크롤 경계에 잘리지 않게 2px 여유(바깥 여백으로 되돌림) */
const PcBody = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    margin: -2px;
    padding: 2px;
    word-break: keep-all;
    overflow-wrap: anywhere;
`;

const PcFooter = styled.div`
    flex-shrink: 0;
    padding-top: 24px;
`;

/** 폰 시트 제목 — 공용 시트 제목(19px 800) 그대로, 긴 확인 문구는 줄바꿈 */
const SheetTitleText = styled.span`
    display: block;
    white-space: pre-line;
    word-break: keep-all;
    overflow-wrap: anywhere;
`;

/** 폰 시트 본문 — 제목 아래 4px(버튼 줄 위 여백은 시트 본문 아래 12px + 버튼 줄 위 12px). 한국어는 어절 단위로 줄바꿈 */
const SheetBody = styled.div`
    padding-top: 4px;
    word-break: keep-all;
    overflow-wrap: anywhere;
`;

const renderChildren = (children: SheetDialogProps['children'], controls: SheetDialogControls): React.ReactNode =>
    typeof children === 'function' ? children(controls) : children;

const SheetDialogOpen = ({ title, onClose, busy = false, footer, pcWidth = 400, disableScrollLock = false, sheetZIndex, children }: Omit<SheetDialogProps, 'open'>) => {
    const mobile: boolean = useIsMobileWidth();
    const busyRef = useRef<boolean>(busy);
    busyRef.current = busy;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    /** done(after) 로 닫는 중이면 그 뒤에 할 일 */
    const afterRef = useRef<(() => void) | null>(null);

    // 폰 + 잠금 끔: 공용 시트의 몸통 고정(useBodyScrollLock, passive effect)보다 먼저(layout effect) 잠김 표시만 단다
    useLayoutEffect(() => {
        if (!mobile || !disableScrollLock) return undefined;
        const body: HTMLElement = document.body;
        if (body.dataset.scrollLock === 'true') return undefined;
        body.dataset.scrollLock = 'true';
        return () => {
            delete body.dataset.scrollLock;
        };
    }, [mobile, disableScrollLock]);

    /** 처리 중엔 바깥·끌기·뒤로가기·Esc 로 닫지 않는다(스스로 닫는 done 은 통과) */
    const guardedClose = (): void => {
        if (busyRef.current) return;
        onCloseRef.current();
    };

    if (mobile) {
        const controlsOf = (panel: FilterPanelControls): SheetDialogControls => ({
            close: panel.close,
            done: (after: () => void) => {
                afterRef.current = after;
                panel.apply();
            },
        });
        return (
            <FilterPanel
                open
                anchorEl={null}
                title={<SheetTitleText>{title}</SheetTitleText>}
                onClose={guardedClose}
                onApply={() => {
                    const after: (() => void) | null = afterRef.current;
                    afterRef.current = null;
                    if (after) after();
                    else onCloseRef.current();
                }}
                sheetZIndex={sheetZIndex}
                // 입력칸이 있는 창(탈퇴 비밀번호·쿠폰 코드): 키보드가 뜨면 시트가 보이는 높이 안에 들어오고(위로 넘치면 시트 안 스크롤·상태 표시줄 아래),
                // 버튼 줄은 키보드에 붙고, 포커스된 칸은 시트 안에서 보이게(UI 가이드 7.1). 입력칸이 없는 확인 창은 키보드가 뜨지 않아 그대로다
                keyboardSafeTop
                keyboardFit
                renderFooter={footer ? (panel: FilterPanelControls) => footer(controlsOf(panel)) : undefined}
            >
                {(panel: FilterPanelControls) => <SheetBody>{renderChildren(children, controlsOf(panel))}</SheetBody>}
            </FilterPanel>
        );
    }

    const controls: SheetDialogControls = {
        close: () => onCloseRef.current(),
        done: (after: () => void) => after(),
    };
    return (
        <Modal
            open
            onClose={guardedClose}
            disableScrollLock={disableScrollLock}
            sx={{ zIndex: designSystem.zIndex.modal, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-labelledby="sheet-dialog-title"
        >
            <PcCard role="dialog" aria-modal="true" aria-labelledby="sheet-dialog-title" tabIndex={-1} style={{ width: pcWidth }}>
                <PcTitle id="sheet-dialog-title">{title}</PcTitle>
                <PcBody>{renderChildren(children, controls)}</PcBody>
                {footer && <PcFooter>{footer(controls)}</PcFooter>}
            </PcCard>
        </Modal>
    );
};

/** 공용 확인·입력 창(폰 시트 / PC 작은 창) — 열려 있을 때만 그린다 */
const SheetDialog = ({ open, ...rest }: SheetDialogProps) => (open ? <SheetDialogOpen {...rest} /> : null);

export default SheetDialog;
